import type { IconName } from '@/shared/ui';
import { routes } from './routes';

/** Every screen that can appear in the header title, tabs or navigation. */
export type PageKey =
  | 'practiceDashboard'
  | 'practiceReport'
  | 'appointmentStatistics'
  | 'newPatientStatistics'
  | 'utilization'
  | 'patientMap'
  | 'versionHistory'
  | 'users'
  | 'userAdd'
  | 'userEdit'
  | 'tickets'
  | 'ticketCreate'
  | 'ticketDetail'
  | 'botConfiguration'
  | 'scriptRunner'
  | 'loginHistory'
  | 'profile'
  | 'analyticsCalls'
  | 'analyticsAppointmentTracking'
  | 'analyticsLocationSite'
  | 'analyticsReviews';

/**
 * Who may see a navigation entry. Evaluated by the session entity (`canAccess`):
 * - `all`: every signed-in user
 * - `admin`: ADMIN, SUPERADMIN
 * - `botConfiguration`: admins, or users whose permissionType is BOT_CONFIGURATION
 *
 * Analytics-only users (permissionType ANALYTICS) are additionally limited to the marketing
 * group, see `useVisibleNav`.
 */
export type AccessRule = 'all' | 'admin' | 'botConfiguration';

export interface NavItem {
  key: PageKey;
  path: string;
  icon: IconName;
  access: AccessRule;
}

export interface NavGroup {
  key: 'overview' | 'analytics' | 'operations' | 'marketing';
  items: NavItem[];
}

/** Metadata attached to routes (`handle`) for the header title and tabs. */
export interface RouteHandle {
  page: PageKey;
  icon: IconName;
}

// Grouping and order follow the client's prototype.
export const navGroups: NavGroup[] = [
  {
    key: 'overview',
    items: [
      { key: 'practiceDashboard', path: routes.practiceDashboard, icon: 'chart', access: 'all' },
      {
        key: 'appointmentStatistics',
        path: routes.appointmentStatistics,
        icon: 'calendar',
        access: 'all',
      },
      {
        key: 'newPatientStatistics',
        path: routes.newPatientStatistics,
        icon: 'usersplus',
        access: 'all',
      },
    ],
  },
  {
    key: 'analytics',
    items: [
      { key: 'utilization', path: routes.utilization, icon: 'gauge', access: 'all' },
      { key: 'patientMap', path: routes.patientMap, icon: 'map', access: 'all' },
      { key: 'versionHistory', path: routes.versionHistory, icon: 'history', access: 'all' },
    ],
  },
  {
    key: 'operations',
    items: [
      { key: 'users', path: routes.users, icon: 'user', access: 'admin' },
      { key: 'tickets', path: routes.tickets, icon: 'ticket', access: 'all' },
      {
        key: 'botConfiguration',
        path: routes.botConfiguration,
        icon: 'bot',
        access: 'botConfiguration',
      },
      {
        key: 'scriptRunner',
        path: routes.scriptRunner,
        icon: 'bot',
        access: 'botConfiguration',
      },
    ],
  },
  {
    key: 'marketing',
    items: [
      { key: 'analyticsCalls', path: routes.analyticsCalls, icon: 'phone', access: 'all' },
      {
        key: 'analyticsAppointmentTracking',
        path: routes.analyticsAppointmentTracking,
        icon: 'funnel',
        access: 'all',
      },
      {
        key: 'analyticsLocationSite',
        path: routes.analyticsLocationSite,
        icon: 'globe',
        access: 'all',
      },
      { key: 'analyticsReviews', path: routes.analyticsReviews, icon: 'star', access: 'all' },
    ],
  },
];
